import React, { useRef, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ThirdPartyConfigsSync as RovoThirdPartyConfigsSync } from '@atlassian/rovo-connectors-config/configs-sync';
import { ProductKeys3P as RovoProductKeys3P } from '@atlassian/rovo-connectors-config/types';

// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import {
	type NounKeys,
	NounKeys as NounKeysValue,
	nouns,
	NOUNS_ORDER,
} from '../../../../constants/nouns';
import { type ProductKeys, ProductKeys as ProductKeysObject } from '../../../../constants/products';
import { onAppFilterOptionClicked } from '../../../../utils/events/product-option-clicked';
import { onAppFilterOptionHovered } from '../../../../utils/events/right-panel-app-filter-events';
import { serveNounResultCounts } from '../../../../utils/experiments';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import FilterOption from '../filter-option';

const getAtlassianWebCrawlerConfig = () => {
	return RovoThirdPartyConfigsSync.get(RovoProductKeys3P.AtlassianWebCrawler);
};

interface FilterDataOption {
	queryParamValue: string;
	isSelected: boolean;
}

export interface NounFilterProps {
	availableProducts: ProductKeys[];
	availableNouns: NounKeys[];
	selectedProduct?: ProductKeys;
	onSelectProduct: (productKey: ProductKeys | undefined, datasource?: string) => void;
	setAtlasNoun: (noun?: NounKeys) => void;
	setWebPageType: () => void;
	filterData?: {
		options: FilterDataOption[];
	};
	productResultCountMap: { [key: string]: number };
	resultCountsLoading?: boolean;
}

const dividerStyles = xcss({
	width: '100%',
	borderBlockEnd: 'none',
	borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
	borderInline: 'none',
	marginTop: 'space.075',
	marginBottom: 'space.100',
});

export const Divider = (): React.JSX.Element => <Box xcss={dividerStyles} />;

export const NounFilters = ({
	availableProducts,
	availableNouns,
	selectedProduct,
	setAtlasNoun,
	setWebPageType,
	filterData,
	productResultCountMap = {},
	resultCountsLoading = false,
}: NounFilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const shouldShowNouns: boolean = useMemo(() => {
		const isWebCrawlerAvailable = availableProducts.includes(ProductKeysObject.AtlassianWebCrawler);
		return availableNouns.length > 0 || isWebCrawlerAvailable;
	}, [availableNouns, availableProducts]);

	const nounFilters = useMemo(() => {
		if (
			getAtlassianWebCrawlerConfig()?.fanOutConnectorSources &&
			fg('webcrawler-fanout-ui-sidebar-enabled')
		) {
			const isAtlasSelected = selectedProduct === ProductKeysObject.Atlas;
			const isNounSelected = (noun: NounKeys) => {
				return (
					(isAtlasSelected &&
						filterData?.options.find((option) => option.queryParamValue === noun)?.isSelected) ??
					false
				);
			};

			const nounsToDisplay = NOUNS_ORDER.filter((noun) => availableNouns.includes(noun));

			return nounsToDisplay.map((noun) => ({
				label: formatMessage(nouns[noun].displayNamePlural),
				value: noun,
				isSelected: isNounSelected(noun),
				onSelect: () => setAtlasNoun(noun),
			}));
		} else {
			const isWebCrawlerAvailable = availableProducts.includes(
				ProductKeysObject.AtlassianWebCrawler,
			);
			const isAtlasSelected = selectedProduct === ProductKeysObject.Atlas;

			const isWebCrawlerSelected = selectedProduct === ProductKeysObject.AtlassianWebCrawler;
			const isNounSelected = (noun: NounKeys) => {
				const result =
					((isWebCrawlerSelected && noun === NounKeysValue.AtlassianWebCrawler) ||
						(isAtlasSelected &&
							filterData?.options.find((option) => option.queryParamValue === noun)?.isSelected)) ??
					false;

				return result;
			};
			const NOUNS_ORDER_WITH_WEBCRAWLER = isWebCrawlerAvailable
				? NOUNS_ORDER
				: NOUNS_ORDER.filter((noun) => noun !== NounKeysValue.AtlassianWebCrawler);

			const atlasNouns = availableNouns;
			const nounsToDisplay = NOUNS_ORDER_WITH_WEBCRAWLER.filter(
				(noun) =>
					atlasNouns.includes(noun) ||
					(isWebCrawlerAvailable && noun === NounKeysValue.AtlassianWebCrawler),
			);
			const shouldServeNounResultCounts = serveNounResultCounts();

			return nounsToDisplay.map((noun) => {
				const isSelected = isNounSelected(noun);

				return {
					label: formatMessage(nouns[noun].displayNamePlural),
					value: noun,
					isSelected,
					count: shouldServeNounResultCounts ? productResultCountMap[noun] : undefined,
					countLoading: shouldServeNounResultCounts ? resultCountsLoading : false,
					//web crawler is rendered as a noun, but queried as a product
					onSelect:
						noun === NounKeysValue.AtlassianWebCrawler
							? () => setWebPageType()
							: () => setAtlasNoun(noun),
				};
			});
		}
	}, [
		setAtlasNoun,
		setWebPageType,
		selectedProduct,
		filterData,
		availableProducts,
		availableNouns,
		formatMessage,
		resultCountsLoading,
		productResultCountMap,
	]);

	if (!shouldShowNouns || !nounFilters.length) {
		return null;
	}

	return (
		<Stack>
			<Divider />
			<Box role="list">
				{nounFilters.map((option, index) => (
					<FilterOption
						key={option.value}
						option={option}
						onClick={() => {
							fireAnalyticsEvent(
								onAppFilterOptionClicked({
									commonAttributes,
									nonPrivacySafeAttributes,
									productKey: option.value as ProductKeys,
									isConnected: true, // nouns are always 1p/connected
									index,
								}),
							);
							option.onSelect();
						}}
						onHover={() => {
							if (hoverTimeoutRef.current) {
								clearTimeout(hoverTimeoutRef.current);
							}
							hoverTimeoutRef.current = setTimeout(() => {
								fireAnalyticsEvent(
									onAppFilterOptionHovered({
										commonAttributes,
										nonPrivacySafeAttributes,
										productKey: option.value as ProductKeys,
										isConnected: true, // nouns are always 1p/connected
										index,
									}),
								);
							}, 500);
						}}
						ariaRole="listitem"
					/>
				))}
			</Box>
		</Stack>
	);
};
